import { useEffect, useRef, useState } from 'react';
import { Share } from '@capacitor/share';
import {CameraPreviewOptions, CameraPreview} from '@capacitor-community/camera-preview';
import { Camera } from '@capacitor/camera';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Modal } from 'antd';
import { AndroidSettings, NativeSettings } from 'capacitor-native-settings';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { ScreenRecorder } from '@srikant-kumar/capacitor-screen-recorder';
import { StatusBar } from '@capacitor/status-bar';

import {ReactComponent as RecordOnIcon} from '../../assets/icons/record-on.svg';
import {ReactComponent as RecordOffIcon} from '../../assets/icons/record-off.svg';

import videoDarkPlurImg from '../../assets/images/video-dark-blur.png';
import videoLightPlurImg from '../../assets/images/video-light-blur.png';
import guitarLogo from '../../assets/images/camera-guitar-logo.png';
import { useStore } from '../../store';
import CustomButton from './CustomButton';
import config from '../../config';
import { PushNotifications } from '@capacitor/push-notifications';
import { App } from '@capacitor/app';
import { ButtonBase } from '@mui/material';
import { PluginListenerHandle } from '@capacitor/core';
import { getPlatform } from '../../utils';
// import VideoCompressor from '../../plugins/videoCompressorPlugin';

export default function VideoCapture({lightTheme} : {lightTheme: boolean}) {
  const {store: {cameraPreviewOn, text, recording}, updateStoreFunc} = useStore();

  const cameraStart = async() => {
    const cameraPreviewOptions: CameraPreviewOptions = {
      position: 'front',
      height: config.videoHeight,
      // y: 55,
      toBack: true,
      enableHighResolution: true
    };
    try{
      await CameraPreview.start(cameraPreviewOptions);
    }catch(err: any){
      updateStoreFunc('cameraPreviewOn',false);
    }
  }

  const cameraStop = () => {
    CameraPreview.stop();
  }

  const checkCameraPermissionAndStart = async () => {
    const {camera} = await Camera.checkPermissions();
    if(camera === 'denied'){
      goToSetting('camera');
      updateStoreFunc('cameraPreviewOn',false);
      return;
    }

    cameraStart();
  }

  const onRecordingComplete = async (filePath: string, fileName: string) => {
    Modal.confirm({
      title: text["Share Recording?"],
      content: text["The recording has been saved in the /Movies directory. Would you like to share it now?"],
      okText: text["Share"],
      cancelText: text["No"],
      centered: true,
      onOk() {
        shareRecording(filePath);
      },
    });
  }

  const shareRecording = async (filePath: string) => {
    const {publicStorage} = await Filesystem.checkPermissions();
      
    if(publicStorage === 'denied'){
      goToSetting('storage');
      return;
    }

    let requestedStatus;

    if(publicStorage !== 'granted') {
        requestedStatus = await Filesystem.requestPermissions();
        if(requestedStatus?.publicStorage !== "granted") return;
    }

    Share.share({
      url: "file:///" + filePath,
    });
  }

  const goToSetting = (v: string) => {
    let content : string = "";
    if(v === 'storage'){
      content = text['Please allow the Storage Permission in the app settings to proceed'];
    }

    if(v === 'camera'){
      content = text['Please allow the Camera Permission in the app settings to proceed'];
    }

    if(v === 'audio'){
      content = text['Please allow the Microphone Permission in the app settings to proceed'];
    }

    if(v === 'noti'){
      content = text['Please allow the Notification Permission in the app settings to proceed'];
    }
  
    Modal.confirm({
      title: text['Go To Setting'],
      content: content,
      okText: "Setting",
      cancelText: "Cancel",
      centered: true,
      onOk() {
        if(v === 'storage' || v === 'camera' || v === 'audio'){
          NativeSettings.openAndroid({
            option: AndroidSettings.ApplicationDetails,
          });
        }else if( v === 'noti'){
          NativeSettings.openAndroid({
            option: AndroidSettings.AppNotification,
          });
        }
      },
    });
  }

  const handleRecordClick = async() => {
    updateStoreFunc('recording', !recording);
  }

  const handleRecord = async () => {
    if(recording) {
      try{

        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === 'denied') {
            goToSetting('noti');
            updateStoreFunc('recording', false);
            return;
        }
        
        await ScreenRecorder.start({});
      }catch(err: any){
        console.log("err from screen record", err)
        if(err.message === 'Denied'){ // it means user chose 'Deny and don't ask again'
          goToSetting('audio');
        }
        updateStoreFunc('recording', false);
      }
    }else{
      ScreenRecorder.stop({});
      await StatusBar.show();
    }
  }

  useEffect(()=> {
    if(getPlatform() === 'web') return;
    if(cameraPreviewOn){
      checkCameraPermissionAndStart();
    }else{
      cameraStop();
    }
  },[cameraPreviewOn])

  const awake = async() => {
    const {isKeptAwake} = await KeepAwake.isKeptAwake();
    if(recording){
      if(!isKeptAwake) KeepAwake.keepAwake();
    }else{
      if(isKeptAwake) KeepAwake.allowSleep();
    }
  }


  // const compressVideo = async ( filePath: string, fileName: string ) => {
  //   const ffmpeg = ffmpegRef.current;
  //   const result = await Filesystem.readFile({
  //     path: filePath,
  //     directory: Directory.ExternalStorage,
  //   });
  //   console.log('read result: ', result);
  //   const videoData = Uint8Array.from(atob(result.data as string), (c) => c.charCodeAt(0));
  //   console.log('video data: ', videoData);
  //   const outputFileName = "compressed_output.mp4";
  //   await ffmpeg.writeFile("input.mp4", videoData);
  //   await ffmpeg.exec([
  //     "-i", 
  //     "input.mp4",        // Input file
  //     "-preset", 
  //     "veryfast",         // Specify the preset
  //     "-b:v", 
  //     "1M",               // Specify the video bitrate
  //     outputFileName      // Output file
  //   ]);
  //   const compressedData = await ffmpeg.readFile(outputFileName);
  //   console.log('compressedData: ', compressedData);
  //   const compressedBlob = new Blob([compressedData], { type: "video/mp4" });

  //   console.log("compressedBlob: ", compressedBlob);

  //   const compressedBase64 = await blobToBase64(compressedBlob);

  //   console.log('compressedBase64: ',compressedBase64);

  //   const newFileName = `compressed_${Date.now()}.mp4`;
  //   await Filesystem.writeFile({
  //     path: "/mmGuitar/" + newFileName,
  //     data: compressedBase64,
  //     directory: Directory.ExternalStorage,
  //     recursive: true
  //   });
  //   console.log("Successfully compressed:")
  // }

  async function compressVideo(filePath: string) {
    try {
      // const result = await VideoCompressor.compress({ fileName: filePath });
      // console.log("Compressed video path:", result.compressedPath);
    } catch (error) {
      console.error("Compression failed:", error);
    }
  }

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  useEffect(()=> {
    if(getPlatform() === 'web') return;
    awake();
    handleRecord();
  },[recording])

  useEffect(()=> {
    if(getPlatform() === 'web') return;
    const recordingStartedListener = ScreenRecorder.addListener('onRecordingStarted', (data: any): any => {
      console.log("recording started: ",data);
      (ScreenRecorder as any).enableFullScreen();
      StatusBar.hide();
    });

    const recordingCompletedListener  = ScreenRecorder.addListener('onRecordingComplete', (data: any): any => {
      const filePath = data.file_path;
      const fileName = data.file_name;
      console.log("complete data: ", data);
      // compressVideo(fileName);
      onRecordingComplete(filePath, fileName);
      (ScreenRecorder as any).disableFullScreen();
    });

    const recordingErrorListener = ScreenRecorder.addListener('onRecordingError', (data: any): any => {
      console.log("record err", data);
      updateStoreFunc('recording', false);
    });

    let appStateChangeListener : PluginListenerHandle | null = null;
    App.addListener('appStateChange', (state) => {
      if (!state.isActive) {
        console.log('The app is now in the background or user clicked the home button');
        updateStoreFunc("recording", false);
      } else {
        console.log('The app is now in the foreground');
      }
    }).then(e => appStateChangeListener = e);

    return () => {
      if(getPlatform() === 'web') return;
      updateStoreFunc(null, null, null, (pre) => {
        if(pre.recording){
          ScreenRecorder.stop({});
          StatusBar.show();
          (ScreenRecorder as any).disableFullScreen();
          return {...pre, recording: !pre.recording}
        }else{
          return {...pre, recording: pre.recording}
        }
      })

      recordingStartedListener.remove();
      recordingCompletedListener.remove();
      recordingErrorListener.remove();
      cameraStop();
      updateStoreFunc(null, null, null, (pre => {
        return {...pre, cameraPreviewOn: false}
      }));
      if(appStateChangeListener) appStateChangeListener.remove();
    }
  }, [])

  const blurPos = config.videoHeight - 100 + "px";

  const blurImg = lightTheme ? videoLightPlurImg : videoDarkPlurImg

  const blur = () => {
    return (
      <div>
        {
          cameraPreviewOn && 
          <div>
            <img style={{position: "fixed",top: "350px", left: "10px", zIndex: 1000}} src={guitarLogo}/>
            {/* <CustomButton active={false} style={{position: 'fixed', right: '10px', top: '350px', zIndex: 1000,backgroundColor: "transparent",borderWidth: "0.2px", color: recording? "red" : "white", borderColor: "white", borderStyle: "solid", paddingLeft: "35px",paddingRight: "35px"}}
            onClick={handleRecordClick}>
              {recording ? 'Stop' : 'Record'}
            </CustomButton> */}
            <ButtonBase style={{position: 'fixed', right: '20px', top: '350px', zIndex: 1000}}
            onClick={handleRecordClick}>
              {recording ? <RecordOnIcon/> : <RecordOffIcon/>}
            </ButtonBase>
            <div style={{position: "fixed",top: blurPos, left: "-100px", width: "100%"}}>
              <img src={blurImg}/>
            </div>
          </div>
        }
      </div>
    )
  }

  return (
    <div>
        {blur()}
    </div>
  )
}
